import { Checkbox, Input, InputNumber, Select } from 'antd';
import React, { BaseSyntheticEvent, useMemo } from 'react';
import { ParamKeyValuePair } from 'react-router-dom';

import useSearchParam from '../hooks/useSearchParam';

export interface SearchParamProps<T extends string | string[]> {
  name: string;
  defaultValue?: null | T;
  onSetSearchParams?:
    | ((searchParams: URLSearchParams) => void)
    | ParamKeyValuePair[]
    | Record<string, string>;
  children:
    | React.ReactElement
    | ((props: {
        value: T;
        onChange: (value: T) => void;
        setSearchParam: (value?: T) => void;
      }) => React.ReactElement);
  debounce?: number;
  array?: boolean;
  valuePropName?: string;
  trigger?: string;
  insertOnPressEnter?: boolean;
  insertOnClear?: boolean;
  insertOnSearch?: boolean;
}

const SearchParam = <T extends string | string[]>({
  name,
  defaultValue,
  onSetSearchParams,
  children,
  debounce = 180,
  ...props
}: SearchParamProps<T>) => {
  const [array, valuePropName, trigger, insertOnPressEnter, insertOnClear, insertOnSearch] =
    useMemo(() => {
      let array = false;
      let valuePropName = 'value';
      let trigger = 'onChange';
      let insertOnClear = false;
      let insertOnSearch = false;
      let insertOnPressEnter = false;

      if (typeof children !== 'function') {
        switch (React.Children.only(children).type) {
          case Input.Search:
            trigger = 'onSearch';
            insertOnSearch = true;
            insertOnPressEnter = true;
            break;
          case Input:
            trigger = 'onBlur';
            insertOnPressEnter = true;
            break;
          case InputNumber:
            trigger = 'onChange';
            insertOnPressEnter = true;
            break;
          case Checkbox.Group:
            array = true;
            break;
          case Checkbox:
            valuePropName = 'checked';
            break;
          case Select:
            insertOnClear = true;
            insertOnSearch = Boolean(React.Children.only(children).props?.showSearch);
            break;
        }
      }

      return [
        props.array === undefined ? array : props.array,
        props.valuePropName === undefined ? valuePropName : props.valuePropName,
        props.trigger === undefined ? trigger : props.trigger,
        props.insertOnPressEnter === undefined ? insertOnPressEnter : props.insertOnPressEnter,
        props.insertOnClear === undefined ? insertOnClear : props.insertOnClear,
        props.insertOnSearch === undefined ? insertOnSearch : props.insertOnSearch,
      ];
    }, [
      children,
      props.array,
      props.insertOnClear,
      props.insertOnPressEnter,
      props.insertOnSearch,
      props.trigger,
      props.valuePropName,
    ]);

  const [value, setSearchParam, onChange] = useSearchParam<T>(name, {
    onSetSearchParams,
    debounce,
    array,
  });

  if (typeof children === 'function') {
    return children({ value, onChange, setSearchParam });
  }

  return React.cloneElement(React.Children.only(children), {
    [valuePropName]: defaultValue,
    ...(value !== '' ? { [valuePropName]: value } : {}),
    [trigger]: setSearchParam,
    onChange(e: string | string[] | BaseSyntheticEvent) {
      if (trigger === 'onChange') {
        setSearchParam(e);
      } else {
        onChange(e);
      }
    },
    ...(insertOnClear
      ? {
          onClear() {
            setSearchParam('');
          },
        }
      : {}),
    ...(insertOnSearch
      ? {
          onSearch() {
            setSearchParam();
          },
        }
      : {}),
    ...(insertOnPressEnter
      ? {
          onPressEnter() {
            setSearchParam();
          },
        }
      : {}),
  });
};

export default SearchParam;
